import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { GlobalHotkeys } from './global-hotkeys';
import { MobileTopBar } from './mobile-top-bar';
import { ShellProvider, useShell } from './shell-context';
import { Sidebar } from './sidebar';

/**
 * Both overlays open on a keystroke that most sessions never press, so their
 * chunks are fetched the first time they are needed instead of before the first
 * paint.
 */
const CommandPalette = lazy(() =>
  import('./command-palette').then((module) => ({ default: module.CommandPalette })),
);
const ShortcutsSheet = lazy(() =>
  import('./shortcuts-sheet').then((module) => ({ default: module.ShortcutsSheet })),
);

/**
 * Stays mounted after the first open: unmounting on close would cut the exit
 * animation and re-fetch nothing, since the chunk is already in memory.
 */
function useMountedOnce(open: boolean): boolean {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  return mounted || open;
}

/**
 * White panels floating on a grey canvas, separated by air.
 *
 * The root never scrolls; the scroll lives inside the panel, which is why the
 * sidebar can stay put while a 10.000-row table moves under it.
 */
export function AppShell({ children }: { children: ReactNode }) {
  return (
    <ShellProvider>
      <ShellFrame>{children}</ShellFrame>
    </ShellProvider>
  );
}

function ShellFrame({ children }: { children: ReactNode }) {
  const { mobileNavOpen, setMobileNavOpen, paletteOpen, shortcutsOpen } = useShell();
  const paletteMounted = useMountedOnce(paletteOpen);
  const shortcutsMounted = useMountedOnce(shortcutsOpen);

  return (
    // La caja es la del DOCUMENTO —`theme.css` lo fija en el 100 % y con el
    // desborde cerrado— y no la de la ventana. Medirse en unidades de ventana
    // acá vuelve a abrir la diferencia de un píxel, o la de la barra del
    // navegador móvil, que hacía desplazarse a la página entera.
    <div className="flex h-full w-full flex-col overflow-hidden bg-canvas md:flex-row">
      <MobileTopBar />
      <Sidebar className="hidden md:flex" />

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          showClose={false}
          className="w-[264px] max-w-[85vw] gap-0 border-r-0 bg-sidebar p-2"
        >
          <SheetTitle className="sr-only">Navegación</SheetTitle>
          <Sidebar variant="sheet" />
        </SheetContent>
      </Sheet>

      <main className="m-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background md:ml-0">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin">{children}</div>
      </main>

      <Suspense fallback={null}>
        {paletteMounted && <CommandPalette />}
        {shortcutsMounted && <ShortcutsSheet />}
      </Suspense>
      <GlobalHotkeys />
    </div>
  );
}
